import {
  AdminTableContainer,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Input,
  Label,
  sonnerToast,
  useDocumentTitle,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { apiFetch } from "../../api";
import type {
  FeatureFlagDetail as FeatureFlagDetailType,
  FeatureFlagOverride,
  PortalTenant,
  Project,
} from "../../configTypes";
import { Breadcrumbs } from "./Breadcrumbs";
import { FormBanner } from "./FormBanner";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toDateInputValue(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toISOString().split("T")[0];
}

function isStale(staleAfter: string | null): boolean {
  if (!staleAfter) return false;
  return new Date(staleAfter) < new Date();
}

function FlagEditForm({
  description,
  defaultValue,
  staleAfter,
  createdAt,
  updatedAt,
  saving,
  banner,
  onDescriptionChange,
  onDefaultValueChange,
  onStaleAfterChange,
  onSave,
  onDismissBanner,
}: {
  description: string;
  defaultValue: boolean;
  staleAfter: string;
  createdAt: string;
  updatedAt: string;
  saving: boolean;
  banner: { type: "success" | "error"; message: string } | null;
  onDescriptionChange: (v: string) => void;
  onDefaultValueChange: (v: boolean) => void;
  onStaleAfterChange: (v: string) => void;
  onSave: () => void;
  onDismissBanner: () => void;
}) {
  return (
    <section className="mb-8">
      {banner && (
        <FormBanner type={banner.type} message={banner.message} onDismiss={onDismissBanner} />
      )}
      <div className="mb-3">
        <Label htmlFor="flag-description" className="form-label">
          Description
        </Label>
        <Input
          id="flag-description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className="max-w-md"
        />
      </div>
      <div className="mb-3 flex items-center gap-3">
        <Checkbox
          id="flag-default"
          checked={defaultValue}
          onCheckedChange={(v) => onDefaultValueChange(v === true)}
        />
        <Label htmlFor="flag-default" className="form-label mb-0">
          Default enabled
        </Label>
      </div>
      <div className="mb-4">
        <Label htmlFor="flag-stale" className="form-label">
          Stale after
        </Label>
        <Input
          id="flag-stale"
          type="date"
          value={staleAfter}
          onChange={(e) => onStaleAfterChange(e.target.value)}
          className="max-w-48"
        />
      </div>
      <div className="mb-2 text-xs text-muted-foreground">
        Created {formatDate(createdAt)} &middot; Updated {formatDate(updatedAt)}
      </div>
      <div className="form-actions">
        <Button disabled={saving} onClick={onSave}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </section>
  );
}

function OverridesTable({
  projects,
  overridesByProject,
  onSetOverride,
  onRemoveOverride,
}: {
  projects: Project[];
  overridesByProject: Map<string, FeatureFlagOverride>;
  onSetOverride: (projectId: string, value: boolean) => void;
  onRemoveOverride: (projectId: string) => void;
}) {
  return (
    <section className="mb-8">
      <h2 className="mb-4 text-lg font-semibold text-foreground">Per-Project Overrides</h2>
      <AdminTableContainer aria-label="Per-project overrides">
        {projects.length === 0 ? (
          <EmptyState title="No projects found" />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Project</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => {
                const override = overridesByProject.get(project.id);
                return (
                  <tr key={project.id}>
                    <td>{project.name}</td>
                    <td>
                      {override ? (
                        <Badge variant={override.value ? "success" : "destructive"}>
                          {override.value ? "ON" : "OFF"}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">default</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {!override?.value && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetOverride(project.id, true)}
                          >
                            Enable
                          </Button>
                        )}
                        {(!override || override.value) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetOverride(project.id, false)}
                          >
                            Disable
                          </Button>
                        )}
                        {override && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveOverride(project.id)}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </AdminTableContainer>
    </section>
  );
}

function TenantOverridesTable({
  tenants,
  overridesByTenant,
  onSetOverride,
  onRemoveOverride,
}: {
  tenants: PortalTenant[];
  overridesByTenant: Map<string, FeatureFlagOverride>;
  onSetOverride: (tenantId: string, value: boolean) => void;
  onRemoveOverride: (tenantId: string) => void;
}) {
  return (
    <section>
      <h2 className="mb-4 text-lg font-semibold text-foreground">Per-Tenant Overrides</h2>
      <AdminTableContainer aria-label="Per-tenant overrides">
        {tenants.length === 0 ? (
          <EmptyState title="No tenants found" />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => {
                const override = overridesByTenant.get(tenant.id);
                return (
                  <tr key={tenant.id}>
                    <td>{tenant.name}</td>
                    <td>
                      {override ? (
                        <Badge variant={override.value ? "success" : "destructive"}>
                          {override.value ? "ON" : "OFF"}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">default</span>
                      )}
                    </td>
                    <td>
                      <div className="flex gap-1">
                        {!override?.value && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetOverride(tenant.id, true)}
                          >
                            Enable
                          </Button>
                        )}
                        {(!override || override.value) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onSetOverride(tenant.id, false)}
                          >
                            Disable
                          </Button>
                        )}
                        {override && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onRemoveOverride(tenant.id)}
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </AdminTableContainer>
    </section>
  );
}

function useFeatureFlagState(key: string | undefined) {
  const [flag, setFlag] = useState<FeatureFlagDetailType | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tenants, setTenants] = useState<PortalTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [description, setDescription] = useState("");
  const [defaultValue, setDefaultValue] = useState(false);
  const [staleAfter, setStaleAfter] = useState("");
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (!key) return;
    void Promise.all([
      apiFetch<FeatureFlagDetailType>(`/api/feature-flags/${encodeURIComponent(key)}`),
      apiFetch<Project[]>("/api/projects"),
      apiFetch<PortalTenant[]>("/api/feature-flags/tenants"),
    ])
      .then(([flagData, projectsData, tenantsData]) => {
        setFlag(flagData);
        setDescription(flagData.description);
        setDefaultValue(flagData.defaultValue);
        setStaleAfter(toDateInputValue(flagData.staleAfter));
        setProjects(
          projectsData.filter((p) => !p.archivedAt).sort((a, b) => a.name.localeCompare(b.name)),
        );
        setTenants(tenantsData);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [key]);

  return {
    flag,
    setFlag,
    projects,
    tenants,
    loading,
    error,
    description,
    setDescription,
    defaultValue,
    setDefaultValue,
    staleAfter,
    setStaleAfter,
    saving,
    setSaving,
    banner,
    setBanner,
  };
}

async function saveFlag(key: string, s: ReturnType<typeof useFeatureFlagState>): Promise<void> {
  s.setSaving(true);
  s.setBanner(null);
  try {
    await apiFetch(`/api/feature-flags/${encodeURIComponent(key)}`, {
      method: "PATCH",
      body: JSON.stringify({
        description: s.description,
        defaultValue: s.defaultValue,
        staleAfter: s.staleAfter ? new Date(s.staleAfter).toISOString() : null,
      }),
    });
    s.setFlag((prev) =>
      prev
        ? {
            ...prev,
            description: s.description,
            defaultValue: s.defaultValue,
            staleAfter: s.staleAfter ? new Date(s.staleAfter).toISOString() : null,
          }
        : prev,
    );
    s.setBanner({ type: "success", message: "Flag updated." });
    sonnerToast.success("Flag updated");
  } catch (err) {
    s.setBanner({ type: "error", message: err instanceof Error ? err.message : "Save failed" });
  } finally {
    s.setSaving(false);
  }
}

function useFlagHandlers(key: string | undefined, s: ReturnType<typeof useFeatureFlagState>) {
  async function handleSave() {
    if (!key) return;
    await saveFlag(key, s);
  }

  async function handleSetOverride(projectId: string, value: boolean) {
    if (!key) return;
    try {
      const override = await apiFetch<FeatureFlagOverride>(
        `/api/feature-flags/${encodeURIComponent(key)}/overrides/${projectId}`,
        { method: "PUT", body: JSON.stringify({ value }) },
      );
      s.setFlag((prev) => {
        if (!prev) return prev;
        const exists = prev.overrides.some((o) => o.projectId === projectId);
        const overrides = exists
          ? prev.overrides.map((o) => (o.projectId === projectId ? override : o))
          : [...prev.overrides, override];
        return { ...prev, overrides };
      });
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to set override");
    }
  }

  async function handleRemoveOverride(projectId: string) {
    if (!key) return;
    try {
      await apiFetch(`/api/feature-flags/${encodeURIComponent(key)}/overrides/${projectId}`, {
        method: "DELETE",
      });
      s.setFlag((prev) =>
        prev
          ? { ...prev, overrides: prev.overrides.filter((o) => o.projectId !== projectId) }
          : prev,
      );
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to remove override");
    }
  }

  async function handleSetTenantOverride(tenantId: string, value: boolean) {
    if (!key) return;
    try {
      const override = await apiFetch<FeatureFlagOverride>(
        `/api/feature-flags/${encodeURIComponent(key)}/overrides/tenants/${tenantId}`,
        { method: "PUT", body: JSON.stringify({ value }) },
      );
      s.setFlag((prev) => {
        if (!prev) return prev;
        const exists = prev.overrides.some((o) => o.tenantId === tenantId);
        const overrides = exists
          ? prev.overrides.map((o) => (o.tenantId === tenantId ? override : o))
          : [...prev.overrides, override];
        return { ...prev, overrides };
      });
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to set tenant override");
    }
  }

  async function handleRemoveTenantOverride(tenantId: string) {
    if (!key) return;
    try {
      await apiFetch(
        `/api/feature-flags/${encodeURIComponent(key)}/overrides/tenants/${tenantId}`,
        { method: "DELETE" },
      );
      s.setFlag((prev) =>
        prev ? { ...prev, overrides: prev.overrides.filter((o) => o.tenantId !== tenantId) } : prev,
      );
    } catch (err) {
      sonnerToast.error(err instanceof Error ? err.message : "Failed to remove tenant override");
    }
  }

  return {
    handleSave,
    handleSetOverride,
    handleRemoveOverride,
    handleSetTenantOverride,
    handleRemoveTenantOverride,
  };
}

function FlagDetailBody({
  flag,
  s,
  handlers,
}: {
  flag: FeatureFlagDetailType;
  s: ReturnType<typeof useFeatureFlagState>;
  handlers: ReturnType<typeof useFlagHandlers>;
}) {
  const overridesByProject = new Map(
    flag.overrides
      .filter((o): o is typeof o & { projectId: string } => o.projectId !== null)
      .map((o) => [o.projectId, o]),
  );
  const overridesByTenant = new Map(
    flag.overrides
      .filter((o): o is typeof o & { tenantId: string } => o.tenantId !== null)
      .map((o) => [o.tenantId, o]),
  );

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[{ label: "Feature Flags", to: "/config/feature-flags" }, { label: flag.key }]}
      />
      <div className="mb-6 flex items-center gap-3">
        <h2 className="font-mono text-2xl text-foreground">{flag.key}</h2>
        {isStale(flag.staleAfter) && <Badge variant="warning">Stale</Badge>}
      </div>
      <FlagEditForm
        description={s.description}
        defaultValue={s.defaultValue}
        staleAfter={s.staleAfter}
        createdAt={flag.createdAt}
        updatedAt={flag.updatedAt}
        saving={s.saving}
        banner={s.banner}
        onDescriptionChange={(v) => {
          s.setDescription(v);
          s.setBanner(null);
        }}
        onDefaultValueChange={(v) => {
          s.setDefaultValue(v);
          s.setBanner(null);
        }}
        onStaleAfterChange={(v) => {
          s.setStaleAfter(v);
          s.setBanner(null);
        }}
        onSave={() => void handlers.handleSave()}
        onDismissBanner={() => s.setBanner(null)}
      />
      <OverridesTable
        projects={s.projects}
        overridesByProject={overridesByProject}
        onSetOverride={(pid, v) => void handlers.handleSetOverride(pid, v)}
        onRemoveOverride={(pid) => void handlers.handleRemoveOverride(pid)}
      />
      <TenantOverridesTable
        tenants={s.tenants}
        overridesByTenant={overridesByTenant}
        onSetOverride={(tid, v) => void handlers.handleSetTenantOverride(tid, v)}
        onRemoveOverride={(tid) => void handlers.handleRemoveTenantOverride(tid)}
      />
    </div>
  );
}

export function FeatureFlagDetail() {
  const { key } = useParams<{ key: string }>();
  const s = useFeatureFlagState(key);
  const handlers = useFlagHandlers(key, s);

  useDocumentTitle(s.flag?.key ?? null, { suffix: " | Simplicity Admin" });

  if (s.loading) return <div className="empty-state">Loading flag...</div>;
  if (s.error || !s.flag) {
    return <div className="empty-state text-destructive">{s.error ?? "Flag not found"}</div>;
  }

  return <FlagDetailBody flag={s.flag} s={s} handlers={handlers} />;
}
